/** Strip non-digits from a clearing or account input. */
export const swedishAccountDigits = (value: string): string =>
	value.replace(/\D/g, "");

/** Strip separators and join clearing + account digits for submit. */
export const mergeSwedishClearingAndAccount = (
	clearing: string,
	account: string
): string =>
	`${swedishAccountDigits(clearing)}${swedishAccountDigits(account)}`;

/**
 * Digit counts accepted per field. Swedish account numbers cannot be checked
 * beyond their length (bank-specific formats, no usable checksum), so these
 * limits are the whole rule — and the inputs cap their entry at the same max.
 */
export const SWEDISH_CLEARING_DIGITS = { min: 4, max: 5 };
export const SWEDISH_ACCOUNT_DIGITS = { min: 7, max: 12 };
export const SWEDISH_FULL_ACCOUNT_DIGITS = { min: 11, max: 17 };

const hasDigitCountWithin = (
	{ min, max }: { min: number; max: number },
	value: string
): boolean =>
	new RegExp(`^\\d{${min},${max}}$`).test(swedishAccountDigits(value));

/** Swedish clearing number on its own. */
export const isValidSwedishClearingNumber = (value: string): boolean =>
	hasDigitCountWithin(SWEDISH_CLEARING_DIGITS, value);

/** Swedish account number without its clearing number. */
export const isValidSwedishAccountNumber = (value: string): boolean =>
	hasDigitCountWithin(SWEDISH_ACCOUNT_DIGITS, value);

/** Whole Swedish account number: clearing and account together. */
export const isValidSwedishFullAccountNumber = (value: string): boolean =>
	hasDigitCountWithin(SWEDISH_FULL_ACCOUNT_DIGITS, value);

/**
 * Whether the account number in the form was typed by the customer. A value
 * that came from FLOW, Tink or the trusted-account select is the backend's to
 * answer for, so the length rules above only apply to manual entry.
 */
export const isManuallyEnteredSwedishAccount = ({
	accountNumber,
	prefilledAccountNumber,
	isWholeAccountNumber,
	hasMultipleTrustedAccounts,
}: {
	accountNumber: string;
	prefilledAccountNumber: string;
	isWholeAccountNumber: boolean;
	hasMultipleTrustedAccounts: boolean;
}): boolean => {
	// The select only offers numbers FLOW sent, so nothing there is typed.
	if (hasMultipleTrustedAccounts) return false;
	if (!isWholeAccountNumber) return true;

	// Compared on digits so a difference in separators alone is not an edit.
	return (
		swedishAccountDigits(accountNumber) !==
		swedishAccountDigits(prefilledAccountNumber)
	);
};

export const resolveSwedishAccountNumberForSubmit = (
	{
		accountNumber,
		clearingNumber,
	}: { accountNumber: string; clearingNumber: string },
	isWholeAccountNumber: boolean
): string =>
	isWholeAccountNumber
		? swedishAccountDigits(accountNumber)
		: mergeSwedishClearingAndAccount(clearingNumber, accountNumber);
